// Through the barrel for anything with a class hierarchy behind it, exactly as
// BloodworkRange and CustomMarker do: PanelRange.ts itself imports from '.',
// so reaching it directly would make this module the entry point of the cycle.
// Nothing here touches these until a function runs, by which point the barrel
// has finished.
import {
  Bloodwork,
  BloodworkAnalysis,
  BloodworkRange,
  Client,
  PanelField,
  PanelRange,
  SECTION_PANEL_MARKER_GETTERS,
} from '.';
import { visibleExceptProd } from '../utils/featureVisibility';
// Direct, not through the barrel: these are leaves with no imports of their
// own, and Section is read at module-evaluation time below (the section walk
// order, the home overrides), which a barrel binding cannot promise to have
// ready.
import { Section } from './Section';
import { Gender } from './Gender';
import { ABOUT_MARKER_MAP } from './about-marker-map';
import {
  UnitSystem,
  precisionFor,
  toDisplayValue,
  unitsFor,
} from './units/unitConversions';
import { MathUtils } from '../utils/MathUtils';

/**
 * The BloodworkRange table, arranged the way a practitioner reads it rather
 * than the way findRange() searches it.
 *
 * A marker is not one range. Ferritin is fifteen rows, split by sex, age band
 * and trimester; hemoglobin is three hundred, because it also splits on the
 * client's elevation. findRange() walks those rows with a client in hand and
 * comes back with one. This module goes the other way: given the rows and no
 * client, it names the population each row is for ("Female 19+", "Male 1–4,
 * African American", "Female, pregnancy, 2nd trimester") and groups them under
 * the marker and the section they belong to.
 *
 * Built from the STORED rows, before init() has derived anything, for two
 * reasons that are the whole point of My Ranges:
 *
 *   - Only six of ferritin's fifteen rows carry an authored optimal. The other
 *     nine get one from arithmetic (PanelRange.init(): lab bound ± a quarter of
 *     the lab width). A practitioner reading the table has to be able to tell
 *     which numbers a clinician chose and which a formula filled in, and once
 *     init() has run nothing can.
 *   - A practitioner's own numbers, when they arrive, go UNDER that arithmetic,
 *     so init() derives around them rather than the override having to undo a
 *     derivation. Same rows, same entry point.
 *
 * The population label and the scope key are two renderings of one value, the
 * RangeScope. That is deliberate: the key is what an override will attach to,
 * and it has to identify a row by what the row MEANS, not by its id (the
 * table's id is not unique and has been rekeyed before). Whatever the label
 * can say, the key can say, and a row the label cannot describe is a row the
 * key would misfile.
 */

// ─── Scope ────────────────────────────────────────────────────────────────────

/**
 * Every discriminator findRange() filters on, as one value.
 *
 * `menstrualPhase` is the one that is not a column. The hormone rows carry it
 * in the attribute name instead ("estradiolE2Follicular"), which is how
 * findRange() reaches them: it appends the client's phase to the attribute
 * before filtering. Here it is lifted back out, so the follicular, luteal and
 * ovulation rows file under Estradiol E2 rather than becoming three markers.
 */
export interface RangeScope {
  gender?: string;
  ageMin?: number;
  ageMax?: number;
  trimester?: number;
  menopause?: string;
  menstrualPhase?: string;
  ethnicity?: string;
  elevationMin?: number;
  elevationMax?: number;
  mpvMin?: number;
  mpvMax?: number;
  /**
   * Leptin's reference interval is published per whole BMI point, so its adult
   * rows split on a BMI window the way platelets split on MPV. It is a
   * discriminator `findRange()` filters on, which is the only test for
   * belonging in here.
   */
  bmiMin?: number;
  bmiMax?: number;
}

/**
 * A stored row's number, or undefined.
 *
 * Some rows store their numbers as STRINGS ('0', '200', '7.5'): a handful of
 * ageMin / ageMax / alarm values arrived that way through the upload script.
 * findRange() never noticed, because `>=` coerces; a label or a key built by
 * string concatenation would notice, and would file '200' and 200 as two
 * different bands.
 */
function toNumber(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function nonBlank(value: unknown): string | undefined {
  if (value == null) return undefined;
  const text = String(value).trim();
  return text ? text : undefined;
}

const MENSTRUAL_PHASE_SUFFIXES = ['Follicular', 'Ovulation', 'Luteal', 'None'];

/**
 * The marker a stored attribute belongs to, and the phase it carries, if any.
 *
 * Only the markers findRange() actually phase-splits are split here, off the
 * same list, so an attribute that merely ends in one of these words ("None"
 * being the dangerous one) is left alone.
 */
export function splitMenstrualPhase(attribute: string): {
  attribute: string;
  menstrualPhase?: string;
} {
  for (const suffix of MENSTRUAL_PHASE_SUFFIXES) {
    if (attribute.length > suffix.length && attribute.endsWith(suffix)) {
      const base = attribute.slice(0, -suffix.length);
      if (PanelRange.MENSTRUAL_PHASE_ATTRIBUTES.includes(base)) {
        return { attribute: base, menstrualPhase: suffix };
      }
    }
  }
  return { attribute };
}

/** The scope a stored row is for. */
export function scopeOf(row: Partial<PanelRange>): RangeScope {
  const scope: RangeScope = {};
  const gender = nonBlank(row.gender);
  if (gender) scope.gender = gender;
  const ageMin = toNumber(row.ageMin);
  const ageMax = toNumber(row.ageMax);
  if (ageMin != null) scope.ageMin = ageMin;
  if (ageMax != null) scope.ageMax = ageMax;
  const trimester = toNumber(row.trimester);
  if (trimester != null) scope.trimester = trimester;
  const menopause = nonBlank(row.menopause);
  if (menopause) scope.menopause = menopause;
  const phase = splitMenstrualPhase(row.attribute || '').menstrualPhase;
  if (phase) scope.menstrualPhase = phase;
  const ethnicity = nonBlank(row.ethnicity);
  if (ethnicity) scope.ethnicity = ethnicity;
  const elevationMin = toNumber(row.elevationMin);
  const elevationMax = toNumber(row.elevationMax);
  if (elevationMin != null) scope.elevationMin = elevationMin;
  if (elevationMax != null) scope.elevationMax = elevationMax;
  const mpvMin = toNumber(row.mpvMin);
  const mpvMax = toNumber(row.mpvMax);
  if (mpvMin != null) scope.mpvMin = mpvMin;
  if (mpvMax != null) scope.mpvMax = mpvMax;
  const bmiMin = toNumber(row.bmiMin);
  const bmiMax = toNumber(row.bmiMax);
  if (bmiMin != null) scope.bmiMin = bmiMin;
  if (bmiMax != null) scope.bmiMax = bmiMax;
  return scope;
}

function band(min?: number, max?: number): string {
  if (min == null && max == null) return '-';
  return `${min == null ? '' : min}-${max == null ? '' : max}`;
}

/**
 * The canonical identity of a population row: the marker plus every
 * discriminator, in a fixed order, with '-' where a row does not split.
 *
 *   ferritin|F|19-200|-|-|-|-|-|-
 *   estradiolE2|F|12-200|-|PRE|Follicular|-|-|-
 *   leptin|F|18-200|-|-|-|-|-|-|bmi:25.5-26.4
 *
 * The attribute is the MARKER's, with the menstrual phase in its own slot, so
 * the key for a follicular row and the key for a luteal row differ where they
 * actually differ. This is what an override will be stored against; it either
 * matches a row that still means what it meant, or matches nothing.
 *
 * The BMI slot is APPENDED, and only on a row that has a BMI band, rather than
 * taking a tenth '-' position on every key. BMI arrived on BloodworkRange
 * after this key did (leptin, the first marker whose interval is published per
 * whole BMI point), and a tenth position would have changed the key of every
 * row in the table — orphaning every override already stored against one, for
 * rows whose meaning had not moved at all. Keys are compared for equality and
 * never parsed, so an optional tail costs nothing.
 */
export function scopeKeyFor(attribute: string, scope: RangeScope): string {
  const { attribute: marker } = splitMenstrualPhase(attribute);
  const key = [
    marker,
    scope.gender || '-',
    band(scope.ageMin, scope.ageMax),
    scope.trimester == null ? '-' : String(scope.trimester),
    scope.menopause || '-',
    scope.menstrualPhase || '-',
    scope.ethnicity || '-',
    band(scope.elevationMin, scope.elevationMax),
    band(scope.mpvMin, scope.mpvMax),
  ].join('|');
  const bmi = band(scope.bmiMin, scope.bmiMax);
  return bmi === '-' ? key : `${key}|bmi:${bmi}`;
}

// ─── Labels ───────────────────────────────────────────────────────────────────

/**
 * The upper bound the table uses to mean "and up". Age bands end at 200,
 * elevation bands at 20,000 ft, MPV bands at 200 fL. None is a real value.
 */
const OPEN_ENDED_AGE = 200;
const OPEN_ENDED_ELEVATION = 20000;
const OPEN_ENDED_MPV = 200;
// Leptin's top band runs to 99, which is past any real body mass index.
const OPEN_ENDED_BMI = 99;

function ageLabel(min?: number, max?: number): string {
  if (min == null && max == null) return '';
  if (min === 0 && max === 0) return 'newborn';
  if (min != null && max != null) {
    if (min === 0 && max >= OPEN_ENDED_AGE) return '';
    if (max >= OPEN_ENDED_AGE) return `${min}+`;
    if (min === max) return `age ${min}`;
    return `${min}–${max}`;
  }
  if (min != null) return `${min}+`;
  return `0–${max}`;
}

const feet = (n: number) => `${n.toLocaleString('en-US')} ft`;

function elevationLabel(min?: number, max?: number): string {
  if (min == null && max == null) return '';
  if (min != null && max != null) {
    if (min === 0 && max >= OPEN_ENDED_ELEVATION) return '';
    if (max >= OPEN_ENDED_ELEVATION) return `${feet(min)}+`;
    if (min === 0) return `up to ${feet(max)}`;
    return `${min.toLocaleString('en-US')}–${feet(max)}`;
  }
  if (min != null) return `${feet(min)}+`;
  return `up to ${feet(max)}`;
}

/**
 * "BMI 25.5–26.4". Same shape as the MPV and elevation labels, because a
 * practitioner reads all three the same way: a window their client either
 * falls in or does not.
 */
function bmiLabel(min?: number, max?: number): string {
  if (min == null && max == null) return '';
  if (min != null && max != null) {
    if (min === 0 && max >= OPEN_ENDED_BMI) return '';
    if (max >= OPEN_ENDED_BMI) return `BMI ${min}+`;
    if (min === 0) return `BMI up to ${max}`;
    return `BMI ${min}–${max}`;
  }
  if (min != null) return `BMI ${min}+`;
  return `BMI up to ${max}`;
}

function mpvLabel(min?: number, max?: number): string {
  if (min == null && max == null) return '';
  if (min != null && max != null) {
    if (min === 0 && max >= OPEN_ENDED_MPV) return '';
    if (max >= OPEN_ENDED_MPV) return `MPV ${min}+`;
    if (min === 0) return `MPV up to ${max}`;
    return `MPV ${min}–${max}`;
  }
  if (min != null) return `MPV ${min}+`;
  return `MPV up to ${max}`;
}

const TRIMESTER_LABEL: Record<number, string> = {
  1: 'pregnancy, 1st trimester',
  2: 'pregnancy, 2nd trimester',
  3: 'pregnancy, 3rd trimester',
};

const MENOPAUSE_LABEL: Record<string, string> = {
  PRE: 'premenopausal',
  POST: 'postmenopausal',
  PREG: 'pregnancy',
};

const PHASE_LABEL: Record<string, string> = {
  Follicular: 'follicular phase',
  Ovulation: 'ovulation',
  Luteal: 'luteal phase',
  // The row findRange() lands on for a premenopausal client whose test
  // recorded no phase. It is a real population, not a gap in the table.
  None: 'no menstrual phase recorded',
};

/**
 * Who a row is for, in words.
 *
 *   {}                                     -> All clients
 *   { gender: 'F', ageMin: 19, ageMax: 200 } -> Female 19+
 *   { gender: 'M', ageMin: 0, ageMax: 0 }    -> Male, newborn
 *   { gender: 'F', trimester: 2 }            -> Female, pregnancy, 2nd trimester
 *
 * Sex and age lead, because they are what every practitioner reasons about
 * first; the rest follow in the order findRange() filters on them.
 *
 * A trimester of 0 is legacy and reads as plain "pregnancy". It was once the
 * "Unknown" choice in the client form, but it never meant "no trimester
 * recorded": every trimester-0 row in the range table is a copy of the
 * trimester-1 row, so the client was being graded against 1st-trimester
 * ranges either way. The form no longer offers it and the stored values were
 * corrected to 1, so no client resolves to these rows any more. The rows
 * themselves are still in the table, which is why the label still handles 0.
 */
export function populationLabel(scope: RangeScope): string {
  const who =
    scope.gender === 'M' ? 'Male' : scope.gender === 'F' ? 'Female' : '';
  const age = ageLabel(scope.ageMin, scope.ageMax);

  let head = '';
  if (who && age) {
    // "Male 19+" reads; "Male newborn" and "Male age 5" do not.
    head = /^\d/.test(age) ? `${who} ${age}` : `${who}, ${age}`;
  } else if (who) {
    head = who;
  } else if (age) {
    head = age.charAt(0).toUpperCase() + age.slice(1);
  }

  const parts: string[] = [];
  if (scope.trimester != null) {
    parts.push(TRIMESTER_LABEL[scope.trimester] || 'pregnancy');
  }
  if (scope.menopause) {
    parts.push(MENOPAUSE_LABEL[scope.menopause] || scope.menopause);
  }
  if (scope.menstrualPhase) {
    parts.push(PHASE_LABEL[scope.menstrualPhase] || scope.menstrualPhase);
  }
  if (scope.ethnicity) {
    parts.push(scope.ethnicity);
  }
  const elevation = elevationLabel(scope.elevationMin, scope.elevationMax);
  if (elevation) parts.push(elevation);
  const mpv = mpvLabel(scope.mpvMin, scope.mpvMax);
  if (mpv) parts.push(mpv);
  const bmi = bmiLabel(scope.bmiMin, scope.bmiMax);
  if (bmi) parts.push(bmi);

  const label = [head, ...parts].filter(Boolean).join(', ');
  return label || 'All clients';
}

// ─── Sections ─────────────────────────────────────────────────────────────────

/**
 * The order sections print in on the report, minus the two that own no
 * markers of their own. Digestion is pattern-driven and borrows every marker it
 * reads from another section; MVX is a parked proof of concept.
 */
export const CATALOG_SECTION_ORDER: Section[] = [
  Section.RBC,
  Section.IRON,
  Section.GLUCOSE,
  Section.LIPIDS,
  Section.THYROID,
  Section.NUTRIENTS,
  Section.PROTEINS,
  Section.WBC,
  Section.INFLAMMATION_OXIDATIVE_STRESS,
  Section.PLATELETS,
  Section.LIVER_BILIARY,
  Section.KIDNEY,
  Section.ELECTROLYTES,
  Section.STRESS_ADRENAL,
  Section.SEX_HORMONE,
  Section.PROSTATE,
];

/**
 * Sections that pull markers from elsewhere to tell a story. They list HDL,
 * ferritin, albumin and a dozen others in ABOUT_MARKER_MAP without being where
 * those markers live, so they claim a marker only when no primary section
 * does. Mirrors AGGREGATOR_LABELS in mooSystemGrouping.ts.
 */
const AGGREGATOR_SECTIONS = new Set<Section>([
  Section.GLUCOSE,
  Section.INFLAMMATION_OXIDATIVE_STRESS,
]);

/**
 * Markers that two primary sections both list, where walking the report order
 * picks the wrong home. Mirrors PRIMARY_OVERRIDES in mooSystemGrouping.ts and
 * its note that "last primary listed wins" is what sends copper, zinc and
 * vitamin A to Nutrients; here the walk is first-wins, so those need
 * naming. Alkaline phosphatase sits in Liver and in WBC (as the zinc proxy) and
 * is a liver marker. Homocysteine is a native inflammation marker that the
 * Nutrients panel also reads. eGFR for African Americans is the one marker
 * with a range row and no About entry at all.
 */
const HOME_SECTION_OVERRIDES: Record<string, Section> = {
  homocysteine: Section.INFLAMMATION_OXIDATIVE_STRESS,
  copperSerumμgdL: Section.NUTRIENTS,
  copperSerummcgmL: Section.NUTRIENTS,
  copperRBCμgdL: Section.NUTRIENTS,
  copperRBCμgmL: Section.NUTRIENTS,
  percentOfFreeCopper: Section.NUTRIENTS,
  vitaminA: Section.NUTRIENTS,
  alkalinePhosphatase: Section.LIVER_BILIARY,
  eGFRforAA: Section.KIDNEY,
};

/**
 * The PanelField a stored attribute displays as, or undefined for a row the
 * app cannot show.
 *
 * Two attribute families do not match a field id verbatim. The hormone rows
 * carry a menstrual phase in the name (see splitMenstrualPhase). The WBC
 * percent differentials are stored as "neutrophilsPercent" and shown through
 * the "neutrophils" field, which is the same rename bloodworkRangesFrom()
 * makes when it builds the analysis set.
 */
export function displayFieldFor(attribute: string): PanelField | undefined {
  const { attribute: marker } = splitMenstrualPhase(attribute);
  const direct = PanelField.findField(PanelField.ALL_FIELDS, marker);
  if (direct) return direct;
  if (marker.endsWith('Percent')) {
    return PanelField.findField(
      PanelField.ALL_FIELDS,
      marker.slice(0, -'Percent'.length),
    );
  }
  return undefined;
}

/** The section a marker files under, or undefined when no section lists it. */
/**
 * Decimal places this marker's bounds print at, in the given system. The
 * catalog rounds to this, so the editor has to agree with it: a value typed at
 * finer precision than the table shows would read back as a change on every
 * open.
 */
export function displayPrecisionFor(
  attribute: string,
  unitSystem: UnitSystem,
): number {
  const field = displayFieldFor(attribute);
  return precisionFor(
    splitMenstrualPhase(attribute).attribute,
    unitSystem,
    field ? field.precision : undefined,
  );
}

export function homeSectionFor(fieldId: string): Section | undefined {
  const override = HOME_SECTION_OVERRIDES[fieldId];
  if (override) return override;
  let aggregator: Section | undefined;
  for (const section of CATALOG_SECTION_ORDER) {
    const ids = ABOUT_MARKER_MAP[section];
    if (!ids || !(fieldId in ids)) continue;
    if (AGGREGATOR_SECTIONS.has(section)) {
      if (!aggregator) aggregator = section;
      continue;
    }
    return section;
  }
  return aggregator;
}

// ─── Editable markers ─────────────────────────────────────────────────────────

/**
 * The markers a practitioner may override, by attribute.
 *
 * An ALLOWLIST, deliberately, and a short one. Every marker in the table can
 * be shown; only these can be argued with, and a marker joins the list by
 * being added here with its test updated, which is the right amount of
 * friction for a change that alters findings on every report. The same shape
 * as the Supplements tab, which a section gets only once its rows are authored
 * and reviewed.
 *
 * The entitlement (`customRangesAllowed`) gates the whole feature on top of
 * this; this gates which markers inside it. Every population of a listed
 * marker is editable, children and pregnancy included (see §6.2 and Phase 4).
 * See my-ranges-spec.md §5.0.
 */
export const EDITABLE_RANGE_ATTRIBUTES: ReadonlySet<string> = new Set([
  /*
   * THE RELEASE CRITERION IS SOURCES, NOT COUNT. A marker joins this list when
   * its bounds are authored in RangeSources.ts, and not before: without them
   * the page shows a column of numbers with no citation on any of them, which
   * is the feature without the argument it exists to make.
   *
   * Ferritin alone shipped the first release (2026-10-12), cut down from five
   * markers listed 2026-09-19 for review on slava. Vitamin D was authored
   * 2026-10-23 and joins here. Glucose, cholesterol and TSH are still
   * unsourced and still off.
   *
   * Narrowing is the dangerous direction. An override stored against a marker
   * later taken off this list keeps grading that practitioner's reports,
   * because `effectiveRanges` has no allowlist check by design, with no page
   * left to see or undo it on. Ferritin has been editable in prod since
   * 2026-10-12, so taking it off now would strand real overrides; vitamin D
   * can still be withdrawn safely until it ships.
   *
   * WIDENING THIS LIST IS ALSO A COPY CHANGE, in two places that are now
   * coupled to the SHAPE of the rows and not only to the names. The page's
   * intro paragraph and the "How My Ranges works" popover both describe the
   * row splits, and ferritin and vitamin D do not split the same way:
   *
   *   ferritin  15 rows: sex, age (including six pediatric bands), menopause,
   *             trimester.
   *   vitaminD   5 rows: sex and trimester only. NO age band at all, so the
   *             adult row is the only row and editing it grades a child on it.
   *
   * That is why "for some markers" came back to both, and why the popover no
   * longer promises that children have their own rows. See isAdultScope below
   * and my-ranges-spec.md §5.1.
   */
  'ferritin', // Iron
  'vitaminD', // Vitamins. HELD OFF PROD, see below.
]);

/**
 * Authored above but NOT released: hidden in prod, live on slava and in dev.
 *
 * The allowlist is what has been AUTHORED; this is what has shipped. Keeping
 * them apart is deliberate, because the two answer different questions and a
 * single list cannot. `EDITABLE_RANGE_ATTRIBUTES` is the record of which
 * markers have their bounds sourced in RangeSources.ts, which is the release
 * criterion and the thing a reader needs when deciding whether to add another.
 * This set is the release GATE, and it empties as each marker goes out.
 *
 * Vitamin D was authored 2026-10-23 and held 2026-10-26 at the user's
 * instruction: the ranges themselves are still under review, so the page must
 * not offer them in prod yet.
 *
 * Gated in `isEditableRangeAttribute()` rather than by building the set
 * conditionally, for two reasons. The set is read directly by tests that pin
 * its contents and by two render tests that ADD a marker to it, so a
 * conditional set would make the authored list invisible to the very tests
 * that guard it. And a module-level env check is evaluated once at import,
 * which cannot be exercised by a test that switches environments; this is
 * evaluated per call.
 *
 * The page filters markers on `editable`, so a held marker does not render at
 * all rather than rendering read-only. That is the intended behavior: a
 * read-only row with no explanation reads as a bug.
 *
 * NOTE WHAT THIS DOES NOT UNDO. `effectiveRanges` has no allowlist check by
 * design, so an override already SAVED against a held marker keeps grading
 * that practitioner's reports with no page left to see it on. Nobody can have
 * stranded one in prod, because vitamin D has never been editable there. On
 * slava it stays visible, so anything saved there is still reachable.
 */
const EDITABLE_RANGE_ATTRIBUTES_HELD_OFF_PROD: ReadonlySet<string> = new Set([
  'vitaminD',
]);

/**
 * The allowlisted markers this environment actually shows, in allowlist order.
 *
 * Exists so the PAGE COPY cannot drift from the gate. The intro paragraph and
 * the help popover both name the markers and describe how their rows split,
 * and that coupling is recorded at both ends. Holding vitamin D off prod broke
 * it in a way a reader would notice immediately: the copy said "Ferritin and
 * vitamin D are the first two" above a list containing only ferritin, and the
 * popover explained vitamin D's missing pediatric rows for a marker that was
 * not on screen. Deriving the sentence from this removes the whole class.
 */
export function releasedEditableRangeAttributes(): string[] {
  return Array.from(EDITABLE_RANGE_ATTRIBUTES).filter(isEditableRangeAttribute);
}

export function isEditableRangeAttribute(attribute: string): boolean {
  const marker = splitMenstrualPhase(attribute).attribute;
  if (!EDITABLE_RANGE_ATTRIBUTES.has(marker)) {
    return false;
  }
  if (EDITABLE_RANGE_ATTRIBUTES_HELD_OFF_PROD.has(marker) && !visibleExceptProd()) {
    return false;
  }
  return true;
}

/**
 * Where LabSmarts' ranges come from: the bibliography page, one section per
 * marker, maintained by hand on the site (scripts/pullBibliography.js reads
 * it). A practitioner deciding whether to argue with a range should be able
 * to see what it rests on first.
 */
export const BIBLIOGRAPHY_URL = 'https://labsmarts.com/bibliography/';

/**
 * The page's anchor per marker. Hand-made on the site, so the casing is
 * whatever was typed ("hba1c", "c-peptide", "T4Free", "FTI") and cannot be
 * derived from the attribute. Every entry was checked against the live page
 * on 2026-09-19. Carries more markers than the allowlist does, from when the
 * list was wider; a marker that comes back onto it finds its anchor waiting.
 *
 * Two markers use their SECTION's anchor rather than one of their own, because
 * each is the first marker in its section's table, so the section anchor
 * lands on it: total cholesterol opens Lipids ("#lipids") and TSH opens
 * Thyroid ("#thyroid").
 */
export const BIBLIOGRAPHY_ANCHORS: Readonly<Record<string, string>> = {
  ferritin: 'ferritin',
  glucose: 'glucose',
  hemoglobinA1c: 'hba1c',
  insulin: 'insulin',
  cpeptide: 'c-peptide',
  cholesterol: 'lipids',
  vitaminD: 'vitamind',
  tsh: 'thyroid',
  t4Total: 'T4Total',
  t4Free: 'T4Free',
  t3Total: 'T3Total',
  t3Free: 'T3Free',
  reverseT3: 'ReverseT3',
  tpoAb: 'TPOAb',
  tgAb: 'TgAb',
  tbg: 'TBG',
  t3Uptake: 'T3Uptake',
  freeT4Index: 'FTI',
};

/** The bibliography section for a marker, or undefined when none is mapped. */
export function bibliographyUrlFor(attribute: string): string | undefined {
  const anchor = BIBLIOGRAPHY_ANCHORS[splitMenstrualPhase(attribute).attribute];
  return anchor ? `${BIBLIOGRAPHY_URL}#${anchor}` : undefined;
}

/**
 * The age at which a row counts as adult. The table's adult bands start at
 * 18 (C-peptide), 19 (ferritin, cholesterol, insulin) or 20 (TIBC, saturation,
 * TSH); a band starting at 16 (ferritin, boys 16–18) is a pediatric one.
 */
export const ADULT_AGE = 18;

/**
 * Whether a row is one an adult client is graded against, and so one a
 * practitioner may override. Pregnancy rows never are; a row with an age
 * band is when the band starts at ADULT_AGE or later.
 *
 * A row with NO age band (or one spanning all of life) counts as adult, and
 * this is the case worth understanding. Glucose, HbA1c, vitamin D and ten of
 * the eleven thyroid markers have one row per sex that covers every age from
 * newborn up: LabSmarts has authored no pediatric range for them, so the adult
 * range IS the only range. Overriding it therefore changes how a child is
 * graded too. The alternative, refusing those rows, would leave most of the
 * allowlist with nothing to edit; the consequence is recorded in
 * my-ranges-spec.md §5.1 instead.
 */
export function isAdultScope(scope: RangeScope): boolean {
  if (scope.trimester != null) return false;
  if (scope.ageMin == null && scope.ageMax == null) return true;
  if (scope.ageMin === 0 && (scope.ageMax == null || scope.ageMax >= 200)) {
    return true;
  }
  return scope.ageMin != null && scope.ageMin >= ADULT_AGE;
}

// ─── Report order ─────────────────────────────────────────────────────────────

let reportOrderCache: Map<Section, string[]> | undefined;

/**
 * The order each section's bar graphs print in, read off the report itself.
 *
 * Each section's markers are listed in a getter on BloodworkAnalysis
 * (getGlucosePanelMarkers and the rest, mapped in SECTION_PANEL_MARKER_GETTERS),
 * and the order of that list IS the order the bars render in. There is no
 * static copy of it to read, and hand-copying seventeen lists here is the
 * kind of list that gets forgotten the day a getter changes. So the lists are
 * read from the getters: one throwaway analysis with every marker set to a
 * value, since the getters drop markers with none, and no ranges, since none
 * are needed to list a marker. Built once per session.
 *
 * Read from a male client so the hormone getter does not append a menstrual
 * phase to its attributes; the phase is stripped anyway, belt and braces.
 */
/**
 * The field a getter's marker attribute displays as. Usually the attribute
 * itself; for the phase-split hormones, whatever the analysis appended to the
 * attribute is dropped, since the field is the marker and not its phase.
 */
function reportAttributeField(attribute: string): string | undefined {
  const direct = displayFieldFor(attribute);
  if (direct) return direct.id;
  const base = PanelRange.MENSTRUAL_PHASE_ATTRIBUTES.find(
    candidate => attribute.startsWith(candidate) && attribute !== candidate,
  );
  return base;
}

export function reportMarkerOrder(): Map<Section, string[]> {
  if (reportOrderCache) return reportOrderCache;

  const client = new Client();
  client.gender = Gender.MALE;
  client.dateOfBirth = '1980-01-01';
  client.elevation = 0;
  const bloodwork = new Bloodwork({
    client,
    collectionDate: '2026-01-01',
    wbcDifferentialUnits: 1,
  } as Partial<Bloodwork>);
  PanelField.PANEL_MARKER_FIELDS.forEach(field => {
    // The one field in the list that is not a number: setting it would append
    // itself to every hormone attribute (see populateSexHormonePanelMarkers).
    if (field.id === 'menstrualPhase') return;
    (bloodwork as any)[field.id] = 1;
  });
  const analysis = new BloodworkAnalysis({
    bloodwork,
    bloodworkRanges: [],
    bloodworkFields: PanelField.ALL_FIELDS,
  });
  analysis.init();

  const order = new Map<Section, string[]>();
  Object.keys(SECTION_PANEL_MARKER_GETTERS).forEach(section => {
    const getter = (analysis as any)[SECTION_PANEL_MARKER_GETTERS[section]];
    if (typeof getter !== 'function') return;
    const attributes: string[] = [];
    (getter.call(analysis) as { attribute?: string }[]).forEach(marker => {
      const id = reportAttributeField(marker.attribute || '');
      if (id && !attributes.includes(id)) attributes.push(id);
    });
    order.set(section as Section, attributes);
  });
  reportOrderCache = order;
  return order;
}

// ─── Catalog ──────────────────────────────────────────────────────────────────

/**
 * The six bounds in the order they sit on the bar graph, low to high, which is
 * also the order My Markers and the per-test custom-markers table lay them out
 * in. Read left to right a row ascends, so a band that has collapsed or an
 * edit that crossed its neighbor shows up as a number out of sequence.
 */
export const RANGE_BOUNDS = [
  'alarmMin',
  'labMin',
  'optimalMin',
  'optimalMax',
  'labMax',
  'alarmMax',
] as const;
export type RangeBound = typeof RANGE_BOUNDS[number];

/**
 * What each bound is called on screen. Shared by the table headings and by
 * every sentence the editor builds ("Changing the lab range also moves Alarm
 * Max from 184.5 to 147"), so a heading and a warning can never use two names
 * for one number.
 */
export const RANGE_BOUND_LABELS: Readonly<Record<RangeBound, string>> = {
  alarmMin: 'Alarm Min',
  labMin: 'Lab Min',
  optimalMin: 'Optimal Min',
  optimalMax: 'Optimal Max',
  labMax: 'Lab Max',
  alarmMax: 'Alarm Max',
};

/** One number in the table, ready to print. */
export interface CatalogBound {
  /** In the practitioner's unit system, rounded for display. */
  value?: number;
  /**
   * Whether LabSmarts wrote this number down, as opposed to init() deriving
   * it from the lab range. Both are what the report uses; only one is a
   * clinician's choice.
   *
   * Always about LABSMARTS' row, never the practitioner's override — a
   * practitioner's own number reports through `overridden` instead, so the
   * page can say which of the three a cell is.
   */
  authored: boolean;
  /** The practitioner typed this one (My Ranges). */
  overridden: boolean;
  /**
   * What LabSmarts resolves this bound to, present only on an overridden row.
   * This is what "replacing LabSmarts' 75" and Reset to LabSmarts are built
   * from, and it is resolved the same way `value` is, so a bound LabSmarts
   * derives compares against the derived number rather than a blank.
   */
  labSmartsValue?: number;
}

/**
 * The numbers an override replaces, by scope key. Only the shape
 * buildRangeCatalog needs: a `RangeOverride` satisfies it, and the catalog
 * does not import the override model, which keeps the dependency one-way
 * (RangeOverride -> RangeCatalog) with no cycle between them.
 */
export type RangeBoundValues = Partial<Record<RangeBound, number>>;

export interface CatalogRow {
  scopeKey: string;
  label: string;
  scope: RangeScope;
  /**
   * An adult row of an allowlisted marker. The only kind a practitioner can
   * override, and, on the My Ranges page, the only kind shown.
   */
  editable: boolean;
  /** This practitioner has their own numbers for this population. */
  overridden: boolean;
  bounds: Record<RangeBound, CatalogBound>;
}

export interface CatalogMarker {
  /** The MARKER's attribute (phase stripped), e.g. 'estradiolE2'. */
  attribute: string;
  label: string;
  units?: string;
  /** On EDITABLE_RANGE_ATTRIBUTES. Read-only otherwise, whatever the entitlement. */
  editable: boolean;
  /** The marker's section of the bibliography, where its ranges come from. */
  bibliographyUrl?: string;
  rows: CatalogRow[];
}

export interface CatalogSection {
  section: Section;
  markers: CatalogMarker[];
}

export interface RangeCatalog {
  sections: CatalogSection[];
  /**
   * Stored attributes no PanelField displays, left out rather than shown with
   * no name and no units. As of the 2026-09-15 pull that is CRP, nonHDL, vLDL,
   * viscosityHighShear and viscosityLowShear, none of which any report reads.
   * Returned so a test can pin the list and a cleanup can find them.
   */
  skipped: string[];
}

/**
 * Sort order within a marker: the general rows first, pregnancy rows last, and
 * in between the order findRange() filters on, so the table reads the way the
 * lookup thinks.
 */
function compareScopes(a: RangeScope, b: RangeScope): number {
  const genderRank = (g?: string) => (g == null ? 0 : g === 'M' ? 1 : 2);
  // Life order, not alphabetical: PRE before POST.
  const menopauseRank = (m?: string) =>
    m == null ? 0 : m === 'PRE' ? 1 : m === 'POST' ? 2 : 3;
  const num = (n?: number) => (n == null ? -1 : n);
  const str = (s?: string) => s || '';
  return (
    num(a.trimester) - num(b.trimester) ||
    genderRank(a.gender) - genderRank(b.gender) ||
    num(a.ageMin) - num(b.ageMin) ||
    num(a.ageMax) - num(b.ageMax) ||
    str(a.ethnicity).localeCompare(str(b.ethnicity)) ||
    menopauseRank(a.menopause) - menopauseRank(b.menopause) ||
    str(a.menstrualPhase).localeCompare(str(b.menstrualPhase)) ||
    num(a.elevationMin) - num(b.elevationMin) ||
    num(a.mpvMin) - num(b.mpvMin) ||
    num(a.bmiMin) - num(b.bmiMin)
  );
}

/**
 * Arrange the stored rows for reading.
 *
 * Every number is resolved through the same `new BloodworkRange(row).init()`
 * the analysis set is built with, so what prints here is what a report uses,
 * derived bounds included. Values are converted to the practitioner's unit
 * system at this boundary and nowhere else; the rows stay canonical.
 *
 * `overridesByScopeKey` is this practitioner's My Ranges numbers. Given them,
 * every `value` is what their reports are actually graded against, each
 * overridden bound is marked, and `labSmartsValue` carries what the number
 * would be without them. Resolved in ONE place rather than by building the
 * catalog twice: the override has to go on before init() derives (so mean,
 * stdDev and any derived alarm are computed around the practitioner's
 * numbers), and that ordering is easy to get wrong a second time.
 */
export function buildRangeCatalog(
  storedRows: Partial<PanelRange>[],
  unitSystem: UnitSystem = 'US',
  overridesByScopeKey?: ReadonlyMap<string, RangeBoundValues>,
): RangeCatalog {
  const markersByAttribute = new Map<string, CatalogMarker>();
  const sectionByAttribute = new Map<string, Section | undefined>();
  const skipped = new Set<string>();

  (storedRows || []).forEach(row => {
    if (!row || !row.attribute) return;
    const field = displayFieldFor(row.attribute);
    if (!field) {
      skipped.add(row.attribute);
      return;
    }
    const { attribute: marker } = splitMenstrualPhase(row.attribute);
    const scope = scopeOf(row);
    const scopeKey = scopeKeyFor(row.attribute, scope);
    const override = overridesByScopeKey
      ? overridesByScopeKey.get(scopeKey)
      : undefined;
    // The row the practitioner's reports are graded against, and LabSmarts'
    // own, resolved separately so a derived bound compares against a derived
    // bound rather than against a blank. init() runs twice only on a row that
    // carries an override.
    const forAnalysis = new BloodworkRange(row as BloodworkRange);
    if (override) {
      RANGE_BOUNDS.forEach(bound => {
        if (override[bound] != null) forAnalysis[bound] = override[bound];
      });
    }
    const resolved = forAnalysis.init();
    const labSmarts = override
      ? new BloodworkRange(row as BloodworkRange).init()
      : resolved;
    const precision = precisionFor(marker, unitSystem, field.precision);
    const forDisplay = (value: unknown): number | undefined => {
      const shown = toDisplayValue(marker, value as number, unitSystem);
      return shown == null ? undefined : MathUtils.round(shown, precision);
    };

    const bounds = {} as Record<RangeBound, CatalogBound>;
    RANGE_BOUNDS.forEach(bound => {
      bounds[bound] = {
        value: forDisplay(resolved[bound]),
        authored: toNumber(row[bound]) != null,
        overridden: !!override && override[bound] != null,
        labSmartsValue: override ? forDisplay(labSmarts[bound]) : undefined,
      };
    });

    let entry = markersByAttribute.get(marker);
    if (!entry) {
      entry = {
        attribute: marker,
        label: field.label,
        units: unitsFor(marker, unitSystem, field.units),
        editable: isEditableRangeAttribute(marker),
        bibliographyUrl: bibliographyUrlFor(marker),
        rows: [],
      };
      markersByAttribute.set(marker, entry);
      sectionByAttribute.set(marker, homeSectionFor(field.id));
    }
    entry.rows.push({
      scopeKey,
      label: populationLabel(scope),
      scope,
      // Every population of an allowlisted marker, not only the adult ones.
      // Children and pregnancy were held back through Phase 1 (see
      // my-ranges-spec.md §5.1) and unlocked on 2026-10-07 once each of
      // their bounds had a source to show. isAdultScope() still earns its
      // keep: the save dialog uses it to say something extra about a
      // pregnancy row, which is the population where a wrong range costs
      // the most.
      editable: entry.editable,
      overridden: !!override,
      bounds,
    });
  });

  // Markers in the order their bars print in on the report. A marker the
  // section's getter does not list (eGFR for African Americans, the
  // reticulocyte pair) follows the listed ones, in the order the fields are
  // declared in, which is the Add Test Results order.
  const reportOrder = reportMarkerOrder();
  const fieldOrder = new Map<string, number>();
  PanelField.ALL_FIELDS.forEach((field, index) => {
    if (!fieldOrder.has(field.id)) fieldOrder.set(field.id, index);
  });
  const orderOf = (section: Section, marker: CatalogMarker) => {
    const field = displayFieldFor(marker.attribute);
    if (!field) return Number.MAX_SAFE_INTEGER;
    const inReport = (reportOrder.get(section) || []).indexOf(field.id);
    if (inReport >= 0) return inReport;
    const declared = fieldOrder.get(field.id);
    return 100000 + (declared == null ? 99999 : declared);
  };

  const sections: CatalogSection[] = CATALOG_SECTION_ORDER.map(section => ({
    section,
    markers: [] as CatalogMarker[],
  }));
  const bySection = new Map(sections.map(s => [s.section, s]));

  markersByAttribute.forEach(marker => {
    marker.rows.sort((a, b) => compareScopes(a.scope, b.scope));
    const section = sectionByAttribute.get(marker.attribute);
    const home = section ? bySection.get(section) : undefined;
    if (home) {
      home.markers.push(marker);
    } else {
      // A field with no About entry and no override. Nothing today. A marker
      // added tomorrow without either lands here, and the test that pins
      // `skipped` to the known five is what makes that visible rather than a
      // marker quietly missing from the page.
      skipped.add(marker.attribute);
    }
  });

  sections.forEach(s =>
    s.markers.sort((a, b) => orderOf(s.section, a) - orderOf(s.section, b)),
  );

  return {
    sections: sections.filter(s => s.markers.length > 0),
    skipped: Array.from(skipped).sort(),
  };
}
